import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  WhopElementsProvider,
  WhopKycElement,
  WhopVerifications,
  WhopWallet,
} from '../whopElementsAdapter';
import { whopElementsLoader } from '../whopElementsRuntime';
import { walletApi } from '../../../services/api/wallet';
import { WhopWithdrawOverlayBridge } from '../components/WhopWithdrawOverlayBridge';

export type WhopPayoutsPanel = 'kyc' | 'withdraw';

export interface WhopPayoutsModalSession {
  companyId: string;
  accessToken: string;
  influencerId?: string;
  onKycApprovalChange: (approved: boolean) => void;
  onWithdrawalComplete: () => void;
}

interface WhopPayoutsModalContextValue {
  openPayoutsPanel: (session: WhopPayoutsModalSession, panel: WhopPayoutsPanel) => Promise<void>;
}

const WhopPayoutsModalContext = createContext<WhopPayoutsModalContextValue | null>(null);

export function useWhopPayoutsModal(): WhopPayoutsModalContextValue {
  const ctx = useContext(WhopPayoutsModalContext);
  if (!ctx) {
    throw new Error('useWhopPayoutsModal must be used within WhopPayoutsModalProvider');
  }
  return ctx;
}

interface WithdrawWalletConfig {
  companyId: string;
  accessToken: string;
}

interface KycPanelConfig {
  companyId: string;
  accessToken: string;
  influencerId?: string;
  onKycApprovalChange: (approved: boolean) => void;
}

/**
 * Whop allows one consumer-created withdraw element per page — keep a single hidden Wallet mounted.
 * KYC uses embedded Verifications + KycElement (see foodfluence-whop-elements-migration.md).
 */
export const WhopPayoutsModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [panel, setPanel] = useState<WhopPayoutsPanel | null>(null);
  const [withdrawWalletConfig, setWithdrawWalletConfig] = useState<WithdrawWalletConfig | null>(
    null,
  );
  const [kycPanelConfig, setKycPanelConfig] = useState<KycPanelConfig | null>(null);
  const sessionRef = useRef<WhopPayoutsModalSession | null>(null);
  const openPayoutsPanelSeqRef = useRef(0);
  const location = useLocation();

  const invalidateOpenPayoutsPanel = useCallback(() => {
    openPayoutsPanelSeqRef.current += 1;
    setPanel(null);
    setKycPanelConfig(null);
    setWithdrawWalletConfig(null);
  }, []);

  useEffect(() => {
    invalidateOpenPayoutsPanel();
  }, [location.pathname, invalidateOpenPayoutsPanel]);

  const mintElementsToken = useCallback(
    async (nextSession: WhopPayoutsModalSession): Promise<string> => {
      if (nextSession.influencerId) {
        try {
          const refreshed = await walletApi.getAccessToken({
            influencerId: nextSession.influencerId,
          });
          return refreshed.token;
        } catch {
          // Fall back to the bootstrap token when refresh fails.
        }
      }
      return nextSession.accessToken;
    },
    [],
  );

  const closePanel = useCallback(() => {
    setPanel(null);
    setKycPanelConfig(null);
  }, []);

  const openPayoutsPanel = useCallback(
    async (nextSession: WhopPayoutsModalSession, nextPanel: WhopPayoutsPanel) => {
      const openSeq = ++openPayoutsPanelSeqRef.current;
      sessionRef.current = nextSession;
      const accessToken = await mintElementsToken(nextSession);
      if (openSeq !== openPayoutsPanelSeqRef.current) {
        return;
      }

      if (nextPanel === 'withdraw') {
        setWithdrawWalletConfig({
          companyId: nextSession.companyId,
          accessToken,
        });
        setPanel('withdraw');
        return;
      }

      setKycPanelConfig({
        companyId: nextSession.companyId,
        accessToken,
        influencerId: nextSession.influencerId,
        onKycApprovalChange: nextSession.onKycApprovalChange,
      });
      setPanel(nextPanel);
    },
    [mintElementsToken],
  );

  const value = useMemo(() => ({ openPayoutsPanel }), [openPayoutsPanel]);

  const withdrawVisible = panel === 'withdraw';
  const kycVisible = panel === 'kyc' && kycPanelConfig !== null;

  const kycGetToken = useCallback(async (): Promise<string> => {
    if (!kycPanelConfig) {
      return '';
    }
    if (kycPanelConfig.influencerId) {
      try {
        const refreshed = await walletApi.getAccessToken({
          influencerId: kycPanelConfig.influencerId,
        });
        return refreshed.token;
      } catch {
        return kycPanelConfig.accessToken;
      }
    }
    return kycPanelConfig.accessToken;
  }, [kycPanelConfig]);

  return (
    <WhopElementsProvider elements={whopElementsLoader}>
      <WhopPayoutsModalContext.Provider value={value}>
        {children}

        {withdrawWalletConfig ? (
          <Box display="none" aria-hidden>
            <WhopWallet
              key={withdrawWalletConfig.accessToken}
              accountId={withdrawWalletConfig.companyId}
              accessToken={withdrawWalletConfig.accessToken}
            >
              <WhopWithdrawOverlayBridge
                open={withdrawVisible}
                accessToken={withdrawWalletConfig.accessToken}
                sessionRef={sessionRef}
                onClose={closePanel}
              />
            </WhopWallet>
          </Box>
        ) : null}

        <Modal isOpen={kycVisible} onClose={closePanel} size="lg" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent
            borderRadius="16px"
            mx={4}
            aria-label="Identity verification"
            pt={2}
          >
            <ModalCloseButton />
            <ModalBody pb={6} pt={8}>
              {kycPanelConfig ? (
                <WhopVerifications
                  key={kycPanelConfig.companyId}
                  accountId={kycPanelConfig.companyId}
                  kind="individual"
                  analytics={false}
                  getToken={kycGetToken}
                >
                  <WhopKycElement
                    onStatusChanged={({ status }) =>
                      kycPanelConfig.onKycApprovalChange(status === 'approved')
                    }
                    onCompleted={() => {
                      kycPanelConfig.onKycApprovalChange(true);
                      closePanel();
                    }}
                  />
                </WhopVerifications>
              ) : null}
            </ModalBody>
          </ModalContent>
        </Modal>
      </WhopPayoutsModalContext.Provider>
    </WhopElementsProvider>
  );
};
